import React from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Badge,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import XxValueBadgeDisplay from './xx-value-badge-display'
import {getBadgeUrl, isCharmBadge} from '../../../utils/product-utils'

const Badges = ({imageBadges, product, badgeUrlConfig = {}, onNavigate}) => {
    const styles = useMultiStyleConfig('ProductTileStyles')
    if (!imageBadges?.length) {
        return null
    }
    const id = product.id || product.productId
    const charmBadge = imageBadges.find(isCharmBadge)
    const badgesToShow = charmBadge ? [charmBadge] : imageBadges.slice(0, 2)
    const badgeStackStyles = styles?.badgeStack || {}
    const xxValue = product?.c_xxValue || product?.representedProduct?.c_xxValue || ''

    const getStackStyles = () => {
        const hasTextValue = badgesToShow?.some((b) => b?.toLowerCase()?.includes('text-value'))
        const hasXxValue = badgesToShow?.some((b) => b?.toLowerCase()?.includes('xx-value'))
        return hasTextValue
            ? badgeStackStyles?.textValue
            : hasXxValue
            ? badgeStackStyles?.xxValue
            : badgeStackStyles?.default
    }

    return (
        <Stack
            gap="5px"
            position="absolute"
            top="0"
            className="badges-stack"
            left="0"
            padding="10px"
            maxWidth="calc(100% - 40px)"
            pointerEvents="none"
            {...getStackStyles()}
        >
            {badgesToShow?.map((badge, index) => {
                const isTextValue = badge?.toLowerCase()?.includes('text-value')
                const isXxValue = badge?.toLowerCase()?.includes('xx-value')
                const isCharm = isCharmBadge(badge)
                const badgeType = isTextValue
                    ? 'text-value'
                    : isXxValue
                    ? 'xx-value'
                    : isCharm
                    ? 'charm'
                    : 'default'
                const badgeUrl =
                    !isTextValue && !isXxValue ? getBadgeUrl(badge, badgeUrlConfig) : null
                const variantMap = {
                    'text-value': 'textValue',
                    'xx-value': 'xxValue',
                    charm: 'default',
                    default: 'default'
                }
                return (
                    <Badge
                        key={`${id}-${index}`}
                        {...(badgeUrl ? {as: Link, to: badgeUrl} : {})}
                        onClick={
                            badgeUrl
                                ? (event) => {
                                      event.stopPropagation()
                                      onNavigate?.(event, badgeUrl)
                                  }
                                : undefined
                        }
                        variant={variantMap[badgeType]}
                        display={{base: index > 0 ? 'none' : 'block', lg: 'block'}}
                        border="none"
                        pointerEvents={badgeUrl ? 'auto' : 'none'}
                        _hover={{textDecoration: 'none'}}
                        className={`badge-parent badge-${badgeType}`}
                    >
                        {isXxValue && xxValue ? (
                            <XxValueBadgeDisplay xxValue={xxValue} badge={badge} variant="plp" />
                        ) : (
                            <Box
                                className={`badge-box ${isCharm ? 'charm-badge' : ''}`}
                                sx={{
                                    div: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        textWrap: 'auto'
                                    },
                                    p: {
                                        textWrap: 'auto'
                                    },
                                    img: {
                                        width: '15px',
                                        height: '14px',
                                        display: 'none'
                                    },
                                    '&.charm-badge p': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '7px',
                                        textWrap: 'auto'
                                    },
                                    '&.charm-badge img': {
                                        display: 'flex',
                                        width: '12px'
                                    },
                                    '.show-badge': {
                                        display: 'block'
                                    }
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: badge
                                }}
                            />
                        )}
                    </Badge>
                )
            })}
        </Stack>
    )
}

Badges.propTypes = {
    imageBadges: PropTypes.any,
    badgeUrlConfig: PropTypes.objectOf(PropTypes.string),
    onNavigate: PropTypes.func,
    product: PropTypes.shape({
        id: PropTypes.string,
        productId: PropTypes.string,
        c_imageBadges: PropTypes.arrayOf(PropTypes.string),
        c_xxValue: PropTypes.string,
        representedProduct: PropTypes.shape({
            c_xxValue: PropTypes.string
        })
    }).isRequired
}

export default Badges
